import React, { useEffect, useState } from "react";
import { Focus, Performance } from "@dreamhub/designsystem/icons";
import { tabsPropsType } from "@/components/controls/Tabs";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";

const defaultIcons: { [key: string]: JSX.Element } = {
  focus: <Focus />,
  performance: <Performance />
};

export interface DraggableTabsProps extends tabsPropsType {
  enableDragDrop?: boolean;
  onOrderChange?: (newOrder: string[]) => void;
  order?: string[];
  isHomeTabs?: boolean;
  icons?: { [key: string]: JSX.Element };
}

const LayoutTabs: React.FC<DraggableTabsProps> = ({
  options,
  order,
  defaultValue,
  valueSelected,
  className,
  enableDragDrop = false,
  onOrderChange,
  size = "sm",
  tabClassName,
  icons: customIcons
}) => {
  const icons = { ...defaultIcons, ...customIcons };
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [draggedTab, setDraggedTab] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [tabsToRender, setTabsToRender] = useState<string[]>(
    order || Object.keys(options)
  );

  useEffect(() => setSelectedValue(defaultValue), [defaultValue]);
  useEffect(() => {
    setTabsToRender(order || Object.keys(options));
  }, [order, options]);

  const handleSelect = (value: string) => {
    setSelectedValue(value);
    valueSelected && valueSelected(value);
  };

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    value: string
  ) => {
    if (!enableDragDrop) return;
    e.dataTransfer.effectAllowed = "move";
    setDraggedTab(value);
    e.currentTarget.style.cursor = "grabbing";
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    if (!enableDragDrop) return;
    e.currentTarget.style.cursor = "pointer";
    setDraggedTab(null);
    setDropIndex(null);
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    value: string
  ) => {
    if (!enableDragDrop || value === draggedTab) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX;
    const midPoint = rect.left + rect.width / 2;

    const currentIdx = tabsToRender.indexOf(value);
    const newDropIndex = mouseX < midPoint ? currentIdx : currentIdx + 1;

    setDropIndex(newDropIndex);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget?.closest('[data-tabs-container="true"]')) {
      setDropIndex(null);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!enableDragDrop || !draggedTab || dropIndex === null) return;
    e.preventDefault();

    const draggedIdx = tabsToRender.indexOf(draggedTab);

    let adjustedDropIndex = dropIndex;
    // If dropping after the dragged item's position, we need to adjust the index
    if (dropIndex > draggedIdx) {
      adjustedDropIndex--;
    }

    const newOrder = [...tabsToRender];
    newOrder.splice(draggedIdx, 1);
    newOrder.splice(adjustedDropIndex, 0, draggedTab);

    setSelectedValue(draggedTab);
    valueSelected?.(draggedTab);
    onOrderChange?.(newOrder);
    setDropIndex(null);
  };

  return (
    <div
      data-tabs-container="true"
      className={cn(
        "flex w-full border-b-[1px] border-border-border1 px-0 relative overflow-x-auto",
        size === "lg" && "px-2",
        className
      )}
    >
      {enableDragDrop && dropIndex !== null && draggedTab && (
        <div
          className="absolute top-0 bottom-0 w-[1px] bg-cta transition-all duration-200"
          style={{
            left: `${(dropIndex / tabsToRender.length) * 100}%`,
            transform: "translateX(-50%)"
          }}
        />
      )}
      {tabsToRender.map((value: string) => (
        <div
          data-test={`${value}-tab`}
          key={value}
          draggable={enableDragDrop}
          onDragStart={(e) => handleDragStart(e, value)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleDragOver(e, value)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e)}
          onClick={() => handleSelect(value)}
          className="relative transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0"
        >
          <div
            className={cn(
              "flex items-center border-b-[3px] pb-2",
              size === "lg" ? "mt-3 mx-4" : "mx-[10px] mt-[10px]",
              selectedValue === value ? "border-cta" : "border-transparent",
              tabClassName
            )}
          >
            <div className="flex items-center gap-[6px] rounded-lg py-1 px-2 transition-colors duration-200 ease-in-out hover:bg-surface-hover-strong">
              {icons[value] && (
                <IconContainer
                  color={selectedValue === value ? "primary" : "secondary"}
                  size="sm"
                  className={selectedValue === value ? "stroke-2" : ""}
                >
                  {icons[value]}
                </IconContainer>
              )}
              <Text
                as="span"
                variant={
                  selectedValue === value
                    ? size === "lg"
                      ? "lgLabel"
                      : "label"
                    : size === "lg"
                      ? "lgBodySecondary"
                      : "bodySecondary"
                }
              >
                {options[value]}
              </Text>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default LayoutTabs;
